import {
  darkPnlProducing100,
  darkPnlProducing110,
  lightTextPlaceholder,
} from "constants/colors";
import { useBreakpoints } from "constants/constants";
import styled from "styled-components/macro";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  AHEAD_BEHIND_TILE_HEIGHT,
  BEHIND_TARGET_COLOR,
} from "../aheadBehindUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";

const WIDTH = 41;
const MARGIN_RIGHT = 4;
export const PROGRESS_BAR_TOTAL_WIDTH = WIDTH + MARGIN_RIGHT;

const LABEL_FONT_SIZE = 14;
const TARGET_LINE_HEIGHT = 3;
const TARGET_LABEL_TEXT = "Target_Output";
const COUNT_LABEL_TEXT = "Current_Output";
const TARGET_LABEL_TEXT_SHORT = "Target";
const COUNT_LABEL_TEXT_SHORT = "Current";
const OVER_TARGET_COMPLETE_COLOR = darkPnlProducing100;
const OVER_TARGET_INCOMPLETE_COLOR = darkPnlProducing110;
const UNDER_TARGET_INCOMPLETE_COLOR = "#9e3a3a";
const UNDER_TARGET_COMPLETE_COLOR = BEHIND_TARGET_COLOR;

type ProgressBarContentProps = {
  currentProgress: number;
  currentTarget: number;
  totalTarget: number;
};

export default function ProgressBarContent({
  currentProgress,
  currentTarget,
  totalTarget,
}: ProgressBarContentProps) {
  // if currentProgress is beyond the totalTarget, use it to set the target line offset,
  // so the target line can always be measured against the currentProgress
  const barTotal =
    currentProgress > totalTarget ? currentProgress : totalTarget;
  const currentTargetNormalizedPercent = currentTarget / barTotal;
  const targetLineOffsetTopPx =
    AHEAD_BEHIND_TILE_HEIGHT * (1 - currentTargetNormalizedPercent) -
    TARGET_LINE_HEIGHT / 2;

  const currentProgressPercent = (currentProgress / totalTarget) * 100;
  const outputLineOffsetTopPx = Math.max(
    AHEAD_BEHIND_TILE_HEIGHT * (1 - currentProgressPercent / 100) -
      TARGET_LINE_HEIGHT / 2,
    -1.5 * LABEL_FONT_SIZE
  );
  const [flightConfig] = useFlightConfig();
  const processId = useProcessIdOnSingleProcessPage();
  const targetMultColorOnly =
    flightConfig.general.aheadBehindScaleTargetRateColorOnly[
      processId ?? "ALL"
    ] ?? 1;
  const isOverTarget = currentProgress >= currentTarget * targetMultColorOnly;
  const isOverTotalTarget =
    currentProgress >= totalTarget * targetMultColorOnly;

  const completeColor = isOverTarget
    ? OVER_TARGET_COMPLETE_COLOR
    : UNDER_TARGET_COMPLETE_COLOR;
  const incompleteColor = isOverTarget
    ? OVER_TARGET_INCOMPLETE_COLOR
    : UNDER_TARGET_INCOMPLETE_COLOR;

  const { translate } = useFlightTranslation();
  const { isTabletOrLarger } = useBreakpoints();
  const countLabel = isTabletOrLarger
    ? COUNT_LABEL_TEXT
    : COUNT_LABEL_TEXT_SHORT;
  const countLabelText = isOverTotalTarget
    ? translate(countLabel) + "↑"
    : translate(countLabel);
  const targetLabel = isTabletOrLarger
    ? TARGET_LABEL_TEXT
    : TARGET_LABEL_TEXT_SHORT;
  const targetLabelText = translate(targetLabel);

  const doLabelsOverlap =
    Math.abs(targetLineOffsetTopPx - outputLineOffsetTopPx) <
    LABEL_FONT_SIZE / 2;
  return (
    <ProgressBarContentStyles
      {...{
        $completeColor: completeColor,
        $incompleteColor: incompleteColor,
        $progressCompleted: currentProgressPercent,
        $targetLineOffsetTopPx: targetLineOffsetTopPx,
        $outputLineOffsetTopPx: outputLineOffsetTopPx,
        $countLabelText: countLabelText,
        $targetLabelText: targetLabelText,
        $doLabelsOverlap: doLabelsOverlap,
      }}
      data-component-name="Progress Bar"
    >
      <div className="targetLine" />
      <div className="bar-label bar-countLabel">{countLabelText}</div>
      <div className="bar-label bar-targetLabel">{targetLabelText}</div>
    </ProgressBarContentStyles>
  );
}

const ProgressBarContentStyles = styled.div<{
  $completeColor: string;
  $incompleteColor: string;
  $progressCompleted: number;
  $targetLineOffsetTopPx: number;
  $doLabelsOverlap: boolean;
  $outputLineOffsetTopPx: number;
  $countLabelText: string;
  $targetLabelText: string;
}>`
  background: ${props => `linear-gradient(
  to top,
  ${props.$completeColor} 0%,
  ${props.$completeColor} ${props.$progressCompleted}%,
  ${props.$incompleteColor} ${props.$progressCompleted}%,
  ${props.$incompleteColor} 100%
  )`};
  border-radius: 4px;
  /* TODO: replace placeholder width and height */
  width: ${WIDTH}px;
  height: ${AHEAD_BEHIND_TILE_HEIGHT}px;
  margin-right: ${MARGIN_RIGHT}px;
  position: relative;
  .targetLine {
    width: ${WIDTH}px;
    position: absolute;
    top: ${props => props.$targetLineOffsetTopPx}px;
    height: ${TARGET_LINE_HEIGHT}px;
    background: linear-gradient(to right, transparent 50%, #ffffff 50%);
    background-size:
      6px 2px,
      100% 2px;
  }
  .bar-label {
    font-family: "Open Sans";
    font-size: ${LABEL_FONT_SIZE}px;
    position: absolute;
    z-index: 1;
  }
  .bar-targetLabel {
    top: ${props =>
      props.$targetLineOffsetTopPx -
      LABEL_FONT_SIZE / 2 +
      (!props.$doLabelsOverlap
        ? 0
        : props.$targetLineOffsetTopPx > props.$outputLineOffsetTopPx
          ? 10
          : -10)}px;
    left: -${props => props.$targetLabelText.length}ch;
    color: ${lightTextPlaceholder};
  }
  .bar-countLabel {
    top: ${props => props.$outputLineOffsetTopPx - LABEL_FONT_SIZE / 2}px;
    left: -${props => props.$countLabelText.length}ch;
  }
`;
